<template>
    <div>
        <div class="error-boundary" v-if="hasError">
            <v-container fluid>
                <v-row justify="center" align="center" class="fill-height">
                    <v-col cols="12" sm="8" md="6">
                        <v-card class="mx-auto" max-width="500">
                            <v-card-title class="headline red--text">
                                <v-icon color="red" large class="mr-2"
                                    >mdi-alert-circle</v-icon
                                >
                                Oops! Something went wrong
                            </v-card-title>

                            <v-card-text>
                                <p class="mb-4">
                                    We're sorry, but something unexpected
                                    happened. Our team has been notified and is
                                    working to fix this issue.
                                </p>

                                <v-expansion-panels v-if="showDetails">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header>
                                            <span class="font-weight-medium"
                                                >Error Details</span
                                            >
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <pre class="error-details">{{
                                                errorInfo
                                            }}</pre>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-card-text>

                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    @click="reload"
                                    :loading="reloading"
                                >
                                    <v-icon left>mdi-refresh</v-icon>
                                    Try Again
                                </v-btn>
                                <v-btn color="grey" text @click="goHome">
                                    <v-icon left>mdi-home</v-icon>
                                    Go Home
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </div>
        <slot v-else></slot>
    </div>
</template>

<script>
import { captureException, addBreadcrumb } from '@/config/sentry.config'

export default {
    name: 'ErrorBoundary',
    data() {
        return {
            hasError: false,
            error: null,
            errorInfo: null,
            reloading: false,
            showDetails: process.env.NODE_ENV === 'development',
        }
    },
    errorCaptured(err, instance, info) {
        this.hasError = true
        this.error = err
        this.errorInfo = {
            message: err.message,
            stack: err.stack,
            componentInfo: info,
            timestamp: new Date().toISOString(),
        }

        // Add breadcrumb for the error
        addBreadcrumb(
            `Component error in ${instance.$options.name || 'Unknown'}`,
            'error',
            'error'
        )

        // Send to Sentry with component context
        captureException(err, {
            componentError: {
                componentName: instance.$options.name,
                componentInfo: info,
                props: instance.$props,
                route: this.$route ? this.$route.path : 'unknown',
            },
        })

        // Prevent the error from propagating further
        return false
    },
    methods: {
        reload() {
            this.reloading = true

            // Reset error state
            this.hasError = false
            this.error = null
            this.errorInfo = null

            // Add delay to show loading state
            setTimeout(() => {
                this.reloading = false

                // Force component re-render
                this.$forceUpdate()

                // If still has error, reload the page
                if (this.hasError) {
                    window.location.reload()
                }
            }, 1000)
        },
        goHome() {
            this.$router.push('/')
        },
    },
}
</script>

<style scoped>
.error-boundary {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.error-details {
    background-color: #f8f8f8;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    font-size: 12px;
    color: #666;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
